import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ShieldCheck, Truck } from 'lucide-react';
import type { Product } from '@/api/products';
import { cn } from '@/lib/utils';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { sizesForColor } from '../direct-sale-sizes';
import { describeQuoteError } from '../quote-error';
import { DirectSaleMethodPicker } from '../DirectSaleMethodPicker';
import { DirectSaleColorPicker } from './DirectSaleColorPicker';
import { DirectSalePriceBlock } from './DirectSalePriceBlock';
import { DirectSalePriceDetailsDialog } from './DirectSalePriceDetailsDialog';
import { DirectSaleProductDetailsDialog } from './DirectSaleProductDetailsDialog';
import { DirectSaleSizePicker } from './DirectSaleSizePicker';
import { useDesktopCartAction } from './useDesktopCartAction';

export interface DirectSaleSidebarHandle {
  /** Closes the size list; returns whether something was open (for the parent's Escape order). */
  closeTransient: () => boolean;
}

interface DirectSaleDesktopSidebarProps {
  product: Product;
  /** The sizes the chosen colour offers, computed by the parent. */
  sizes: { id: string; name: string }[];
  /** The cart button was clicked without a design: the parent opens the start dialog. */
  onRequestStart: () => void;
  /**
   * Called first on every click on the cart button: the parent ends a text
   * input still going on, stores the canvas as it is now and says whether
   * the product carries a design. Without it the last stored state counts.
   */
  prepareCart?: () => boolean;
}

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * The purchase column of the desktop editor, after Spreadshirt: product,
 * colour and size scroll, price and cart button stay pinned below.
 *
 * The column keeps the layout, the change of colour and the dialogs. What
 * a click on the cart button does lives in `useDesktopCartAction`. Every
 * amount is the quote's own — the column adds nothing up.
 */
export const DirectSaleDesktopSidebar = forwardRef<
  DirectSaleSidebarHandle,
  DirectSaleDesktopSidebarProps
>(function DirectSaleDesktopSidebar(
  { product, sizes, onRequestStart, prepareCart },
  ref
) {
  const { t } = useTranslation();
  const colorId = useDirectSaleStore((s) => s.colorId);
  const quantities = useDirectSaleStore((s) => s.quantities);
  const setColor = useDirectSaleStore((s) => s.setColor);
  const setQuantity = useDirectSaleStore((s) => s.setQuantity);

  const [sizeListOpen, setSizeListOpen] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);

  // A single size shows its stepper in place: there is no list to open,
  // and none that Escape could close.
  const hasSizeList = sizes.length > 1;

  // Only what the colour offers counts: a quantity left over from resumed
  // work never reaches the quote either.
  const pieces = sizes.reduce(
    (sum, size) => sum + (quantities[size.id] ?? 0),
    0
  );

  const {
    quote,
    gallery,
    isSubmitting,
    checkoutError,
    hasDecoration,
    waiting,
    feedback,
    notice,
    setNotice,
    handleCartClick,
  } = useDesktopCartAction({
    product,
    sizes,
    pieces,
    prepareCart,
    onRequestStart,
    onRequestSize: () => {
      if (hasSizeList) setSizeListOpen(true);
    },
  });

  const sizeListShownRef = useRef(false);
  sizeListShownRef.current = hasSizeList && sizeListOpen;
  useImperativeHandle(
    ref,
    () => ({
      closeTransient: () => {
        if (!sizeListShownRef.current) return false;
        setSizeListOpen(false);
        return true;
      },
    }),
    []
  );

  const handleQuantityChange = (sizeId: string, quantity: number) => {
    setNotice(null);
    setQuantity(sizeId, quantity);
  };

  const sizeName = (sizeId: string) =>
    product.variants?.find((v) => v.productSizeId === sizeId)?.productSize
      ?.name ??
    sizes.find((size) => size.id === sizeId)?.name ??
    '';

  const handleColorPick = (nextColorId: string) => {
    if (nextColorId === colorId) return;
    const before = useDirectSaleStore.getState().quantities;
    setColor(
      nextColorId,
      sizesForColor(product, nextColorId).map((size) => size.id)
    );
    const after = useDirectSaleStore.getState().quantities;
    const removed = Object.keys(before).filter(
      (id) => (before[id] ?? 0) > 0 && !after[id]
    );
    if (removed.length > 0) {
      setNotice({
        kind: 'removed',
        text: t('directSale.desktop.sizesRemoved', {
          sizes: removed.map(sizeName).filter(Boolean).join(', '),
        }),
      });
    } else {
      // The list of removed sizes belonged to the previous colour; the
      // request for a size stays until one is chosen.
      setNotice((current) => (current?.kind === 'removed' ? null : current));
    }
  };

  const cartLabel = isSubmitting
    ? t('directSale.checkout.committing')
    : waiting
      ? t('directSale.desktop.priceLoading')
      : pieces > 0
        ? t('directSale.desktop.addToCartCount', { count: pieces })
        : t('directSale.checkout.addToCartLong');

  const showQuoteError = hasDecoration && quote.isError;
  const linkButton = cn(
    'min-h-9 text-left text-sm text-ink underline underline-offset-4',
    focusRing
  );

  return (
    <div className="flex h-full flex-col bg-paper">
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <h2 className="font-display text-[22px] font-bold leading-tight text-ink">
          {product.name}
        </h2>
        <button
          type="button"
          onClick={() => setShowProductDetails(true)}
          className={linkButton}
        >
          {t('directSale.desktop.showProductDetails')}
        </button>

        <DirectSaleColorPicker
          product={product}
          colorId={colorId}
          onPick={handleColorPick}
        />

        <DirectSaleMethodPicker product={product} className="mt-5" />
      </div>

      <div className="flex-none border-t border-line p-6">
        <DirectSaleSizePicker
          sizes={sizes}
          quantities={quantities}
          onQuantityChange={handleQuantityChange}
          open={sizeListOpen}
          onOpenChange={setSizeListOpen}
          notice={notice}
        />

        <div className="mt-4 flex flex-col gap-2 empty:hidden">
          {checkoutError && (
            <p role="alert" className="text-sm text-danger">
              {checkoutError}
            </p>
          )}
          {showQuoteError && (
            <div role="alert" className="flex flex-col items-start gap-1">
              <p className="text-sm text-danger">
                {describeQuoteError(
                  quote.error,
                  t('directSale.checkout.priceFailed')
                )}
              </p>
              <button
                type="button"
                onClick={() => void quote.refetch()}
                className={cn(linkButton, 'font-semibold')}
              >
                {t('directSale.product.retry')}
              </button>
            </div>
          )}
          {feedback === 'unavailable' && (
            <p role="alert" className="text-sm text-danger">
              {t('directSale.desktop.unavailable')}
            </p>
          )}
          {feedback === 'handed-over' && (
            <p
              role="status"
              className="flex items-center gap-2 text-sm font-semibold text-success"
            >
              <Check className="h-4 w-4 flex-none" strokeWidth={2.5} />
              {t('directSale.desktop.handedOver')}
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center gap-4">
          <DirectSalePriceBlock
            product={product}
            hasDecoration={hasDecoration}
            pieces={pieces}
            quote={quote.data}
            quoteIsPlaceholder={quote.isPlaceholderData}
            quoteFailed={quote.isError}
            onShowDetails={() => setShowPriceDetails(true)}
          />
          <button
            type="button"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            onClick={handleCartClick}
            className={cn(
              'h-12 flex-1 rounded-control bg-primary px-4 text-[15px] font-bold text-primary-fg disabled:bg-paper-3 disabled:text-ink-2',
              focusRing,
              'outline-offset-2'
            )}
          >
            {cartLabel}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-ink-2">
          <span className="flex items-center gap-1.5">
            <Truck className="h-4 w-4 flex-none" />
            {t('directSale.checkout.trustShippingTitle')}
          </span>
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 flex-none" />
            {t('directSale.checkout.trustQualityTitle')}
          </span>
        </div>
        <p className="mt-1 text-xs text-mute">
          {t('directSale.checkout.vatShort')}
        </p>
      </div>

      <DirectSaleProductDetailsDialog
        open={showProductDetails}
        onOpenChange={setShowProductDetails}
        product={product}
        sizes={sizes}
        labels={{
          width: t('directSale.desktop.sizeWidth'),
          length: t('directSale.desktop.sizeLength'),
        }}
      />
      {quote.data && (
        <DirectSalePriceDetailsDialog
          open={showPriceDetails}
          onOpenChange={setShowPriceDetails}
          product={product}
          quote={quote.data}
          pieces={pieces}
          previewSlug={gallery[0]?.slug ?? null}
          previewDataUrl={gallery[0]?.preview}
        />
      )}
    </div>
  );
});
